<template>
  <div v-if="user && token" class="followed-businesses-container">
    <section class="trending-section coupons-section">
      <div class="coupons-container">
        <h2>Favorite Businesses</h2>
        <div v-if="businesses.length === 0" class="no-businesses">
          You don't currently have any favorite businesses.
        </div>
        <!-- Desktop horizontal scroll with arrows (only visible on desktop) -->
        <div class="desktop-favorite-businesses desktop-slider-wrapper">
          <button
            v-if="businesses.length > 4"
            class="arrow left-arrow"
            @click="scrollDesktopSlider('left')"
            aria-label="Scroll left"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="none"/>
              <path d="M15 6L9 12L15 18" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="desktop-slider-viewport">
            <div class="businesses-list desktop-slider-scroll" ref="desktopSlider">
              <div
                v-for="(business) in businesses"
                :key="business._id"
                @click="viewBusinessProfile(business._id)"
                class="business-card desktop-slider-card"
              >
                <div class="card-content">
                  <img :src="business.business_logo" alt="Business Logo" class="business-logo" />
                  <h3>{{ business.business_name }}</h3>
                </div>
                <button @click.stop="viewBusinessProfile(business._id)">View Business Profile</button>
              </div>
            </div>
          </div>
          <button
            v-if="businesses.length > 4"
            class="arrow right-arrow"
            @click="scrollDesktopSlider('right')"
            aria-label="Scroll right"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="none"/>
              <path d="M9 6L15 12L9 18" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <!-- Mobile horizontal slider (only visible on mobile) -->
        <div class="mobile-favorite-businesses">
          <div class="mobile-slider-scroll" tabindex="0">
            <div
              v-for="(business) in businesses"
              :key="business._id"
              @click="viewBusinessProfile(business._id)"
              class="business-card"
            >
              <img :src="business.business_logo" alt="Business Logo" class="business-logo" />
              <h3>{{ business.business_name }}</h3>
              <button @click.stop="viewBusinessProfile(business._id)">View Business Profile</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FollowedBusinesses',
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user')),
      token: localStorage.getItem('token'),
      businesses: [],
      showDesktopArrows: false,
    };
  },
  created() {
    if (this.user && this.token) {
      this.fetchFollowedBusinesses();
    }
  },
  mounted() {
    window.addEventListener('resize', this.checkDesktopSliderScrollability);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkDesktopSliderScrollability);
  },
  methods: {
    async fetchFollowedBusinesses() {
      if (this.user && this.user.id && this.token) {
        try {
          const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/businesses/followed`, {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
            params: {
              userId: this.user.id,
            },
          });
          this.businesses = response.data.businesses;
          this.$nextTick(this.checkDesktopSliderScrollability);
        } catch (error) {
          console.error('Error fetching followed businesses:', error);
        }
      }
    },
    viewBusinessProfile(businessId) {
      window.scrollTo(0, 0);
      this.$router.push(`/business/${businessId}`);
    },
    scrollDesktopSlider(direction) {
      const container = this.$refs.desktopSlider;
      if (!container) return;
      const scrollAmount = container.offsetWidth / 4; // 1 card = 25% of container
      if (direction === 'left') {
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    },
    checkDesktopSliderScrollability() {
      this.$nextTick(() => {
        const container = this.$refs.desktopSlider;
        if (!container) {
          this.showDesktopArrows = false;
          return;
        }
        // Only show arrows if more than 4 businesses and scroll is possible
        this.showDesktopArrows = this.businesses.length > 4 && (container.scrollWidth > container.clientWidth + 2);
      });
    },
  },
};
</script>

<style scoped>
.followed-businesses-container {
  padding-top: 20px;
  padding-bottom: 20px;
  font-family: 'Roboto', sans-serif;
}

h2{
  color: white;
}

/* Styles for the gray background container */
.trending-section {
  margin-bottom: 30px;
}

.coupons-section .coupons-container {
  border-radius: 15px;
  border: solid 1px #4C6B30;
  padding: 20px;
}

.coupons-container h2 {
  text-align: left;
  margin-bottom: 20px;
  font-family: 'Roboto', sans-serif;
  font-size: 1.5rem;
}

/* Existing styles */
.no-businesses {
  text-align: center;
  color: #777;
}



/* Desktop horizontal slider with arrows */
.desktop-slider-viewport {
  width: 100%;
  max-width: 100vw;
  overflow: hidden;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
.desktop-slider-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  margin-bottom: 20px;
  gap: 12px; /* Add gap between arrows and slider */
}
.desktop-slider-scroll {
  display: flex;
  flex-direction: row;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none; /* Firefox */
  -webkit-overflow-scrolling: touch;
  min-width: 100%;
  padding-bottom: 8px;
  justify-content: flex-start;
}
.desktop-slider-card {
  flex: 0 0 25%;
  max-width: 25%;
  justify-content: space-between;  /* Changed to space-between */
}
.desktop-slider-scroll::-webkit-scrollbar {
  display: none;
}
/* Card content for vertical stacking of logo and name */
.card-content {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}
.arrow {
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  max-width: 48px;
  max-height: 48px;
  border-radius: 50%;
  background: #5C7B3A;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #A2B885;
  box-shadow: 0 2px 8px rgba(60,60,60,0.10);
  transition: background 0.2s, border 0.2s, box-shadow 0.2s;
  padding: 0;
  outline: none;
  box-sizing: border-box;
}
.arrow svg {
  width: 60%;
  height: 60%;
  display: block;
  margin: auto;
}
.arrow:hover {
  background: #6E9F49;
  border-color: #C2D9A0;
  box-shadow: 0 4px 12px rgba(60,60,60,0.16);
}
.mobile-slider-scroll {
  display: flex;
  flex-direction: row;
  gap: 16px;
  height: 350px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 8px;
  scroll-snap-type: x mandatory;
  scrollbar-width: none; /* Firefox */
}
.mobile-slider-scroll::-webkit-scrollbar {
  display: none;
}
.mobile-slider-scroll > .business-card {
  scroll-snap-align: start;
  min-width: 220px;
  max-width: 220px;
}

.card-content {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
}

.card-content h3 {
  margin: 0 0 10px 0;
  font-size: 1.1rem;
  font-weight: 500;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}
.business-card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  width: 220px;
  max-width: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-width: 220px;
  min-height: 350px;
  max-height: 350px;
  height: 350px;
  box-sizing: border-box;
}

.business-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.business-logo {
  width: 100%;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  margin-bottom: 10px;
  display: block;
}

button {
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  background-color: #4C6B30;
  color: white;
  border: none;
  border-radius: 4px;
  margin-top: auto;
  align-self: stretch;
}

button:hover {
  background-color: #6E9F49;
}

button.load-more-button {
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  background-color: #4C6B30;
  color: white;
  border: none;
  border-radius: 4px;
  margin-top: 20px;
}

button.load-more-button:hover {
  background-color: #6E9F49;
}

.load-more-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* Responsive Styles */
@media (max-width: 768px) {
  .desktop-favorite-businesses {
    display: none !important;
  }
  .mobile-favorite-businesses {
    display: block !important;
    width: 100%;
    margin-bottom: 20px;
  }
  .mobile-slider-scroll > .business-card {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
    margin: 0;
    height: auto;
  }
  .business-logo {
    max-width: 100%;
  }
  button {
    padding: 8px 15px;
    font-size: 14px;
  }
  .coupons-container h2 {
    margin-left: 5%;
  }
  .arrow {
    display: none !important;
  }
}

@media (min-width: 769px) {
  .desktop-favorite-businesses {
    display: flex !important;
  }
  .mobile-favorite-businesses {
    display: none !important;
  }
}
</style>
